/* eslint-disable @typescript-eslint/no-explicit-any */
import { useApolloClient } from '@apollo/client'
import {
  Button,
  Card,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import styled from 'styled-components'
import {
  REMOVE_CATARACT_TYPE_CUSTOMIZATIONS,
  UPDATE_CATARACT_TYPE
} from '../../graphql/queries'
import { useGlobalState } from '../../providers/GlobalState'
import { SuccessButton, WarningButton } from '../Buttons'
import { ButtonBox } from '../Help/HelpLayout'
import { useCataractTypeForm } from './useCataractTypeForm'

interface ParamTypes {
  slug: string
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 90%;
  overflow: auto;
`

const CataractTypeEdit = (): JSX.Element => {
  const { slug } = useParams<ParamTypes>()
  const { cataractTypes, refetch } = useGlobalState()
  const { data, setData, reset, setIsEditing } = useCataractTypeForm()
  const [loading, setLoading] = useState(false)
  const client = useApolloClient()

  useEffect(() => {
    const cataract = cataractTypes.find(c => c?.slug === slug)
    if (!cataract) return
    setIsEditing(true)
    setData(cataract)
    return () => {
      reset()
    }
  }, [slug, cataractTypes])

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value, name } = e.currentTarget

    setData({ ...data, [name]: value })
  }

  const handleCancel = () => {
    const cataract = cataractTypes.find(c => c?.slug === slug)
    if (!cataract) return
    setData(cataract)
  }

  const handleRemove = () => {
    setLoading(true)
    client
      .mutate({
        mutation: REMOVE_CATARACT_TYPE_CUSTOMIZATIONS,
        variables: {
          id: data.id
        }
      })
      .then(() => {
        refetch?.()
        setLoading(false)
      })
  }

  const handleSave = () => {
    setLoading(true)
    const id = data.id
    client
      .mutate({
        mutation: UPDATE_CATARACT_TYPE,
        variables: {
          id,
          input: {
            name: data.name,
            enable: data.enable,
            internalAnnotations: data.internalAnnotations,
            position: +data.position
          }
        }
      })
      .then(() => {
        refetch?.()
        setLoading(false)
      })
  }

  return (
    <Card
      style={{
        position: 'absolute',
        width: 'calc(75% - 20px)',
        background: 'rgba(255,255,255,0.80)',
        height: 'calc(90% - 200px)',
        padding: '16px 16px',
        overflowY: 'auto',
        borderRadius: 4,
        zIndex: 20,
        top: 100,
        left: '50%',
        transform: 'translate(-50%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}
    >
      <Content>
        <Typography variant="h4">Edição de {data?.name}</Typography>
        <br />
        <TextField
          type="text"
          name="name"
          value={`${data?.name}`}
          variant="outlined"
          label="Nome Técnico"
          fullWidth
          onChange={handleChange}
        />
        <FormControlLabel
          control={
            <Switch
              checked={data.enable}
              name="enable"
              color="primary"
              onChange={(e, checked) =>
                handleChange({
                  currentTarget: { value: checked, name: 'enable' }
                } as any)
              }
            />
          }
          label="Ativo a exibição para pacientes"
        />
        <br />
        <TextField
          type="number"
          name="position"
          inputProps={{
            min: 0
          }}
          value={data.position}
          onChange={handleChange}
          variant="outlined"
          label="Posição"
          helperText="Informe um numero 0 a 1000 quanto menor o numero mais ao topo da lista ira aparecer"
        />
        <br />
        <TextField
          name="internalAnnotations"
          onChange={handleChange}
          label="Anotações internas"
          variant="outlined"
          value={data.internalAnnotations || ''}
          helperText="Esse campo é apenas de anotações internas, não será exibido no APP"
          multiline
          fullWidth
          rows={2}
        />
      </Content>
      <ButtonBox
        style={{
          paddingTop: 20,
          borderTop: '1px solid #ccc'
        }}
      >
        <Button
          color="primary"
          onClick={handleRemove}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Aguarde...' : 'Valor Padrão'}
        </Button>
        <WarningButton variant="contained" onClick={handleCancel}>
          Cancelar
        </WarningButton>
        <SuccessButton
          disabled={loading}
          variant="contained"
          onClick={handleSave}
        >
          {loading ? 'Aguarde...' : 'Salvar'}
        </SuccessButton>
      </ButtonBox>
    </Card>
  )
}

export default CataractTypeEdit
